import type { SaveOfferBuilderRoomParams } from '~/hooks/apis/accommodation/useSaveOfferBuilderRoom';

import type { RoomRateRoom } from '../RoomRatesStep/utils';

import {
  getBedGroupDescription,
  getNumberOfBeds,
  getRoomBedGroups,
  getSalesforceRoomSizeSqm,
  getStringValue,
} from './bedding';
import type { SalesforceRoomSource } from './bedding';
import { getRoomAssetDraftId, getRoomAssetId } from './roomAssetMapping';

type RoomDraftPayload = SaveOfferBuilderRoomParams['room'];

export type FlashOfferRoomDraft = RoomDraftPayload & {
  category?: string | null;
  description: string;
  draftId: string;
  existingRoomName?: string | null;
  id?: string;
  name: string;
  property_id: string;
  room_policies?: unknown;
  shouldCreate: boolean;
  size_sqm?: number | null;
};

type EmbeddedRoomTypeSource = {
  _embedded?: {
    room_types?: Array<App.RoomType>;
  };
  room_types?: Array<App.RoomType>;
};

type MapSalesforceRoomsToRoomDraftsParams = {
  propertyCategory?: string | null;
  propertyId: string;
  rooms?: Array<SalesforceRoomSource>;
};

type BuildBlankRoomDraftParams = {
  draftId: string;
  propertyCategory?: string | null;
  propertyId: string;
};

function getRoomDraftName(room: SalesforceRoomSource) {
  return getStringValue(room.room_asset?.name) ?? '';
}

export function getPropertyRoomTypes(property?: (App.Property & EmbeddedRoomTypeSource) | null) {
  return property?.room_types ?? property?._embedded?.room_types ?? [];
}

/**
 * The Salesforce rooms that need a new room type: those with a room asset the AI didn't match to
 * an existing room type (`matchedRoomTypeNameBySalesforceName`, from the AI matcher).
 */
export function getSalesforceRoomsMissingExistingRoomTypes(
  rooms: Array<SalesforceRoomSource> = [],
  matchedRoomTypeNameBySalesforceName: Record<string, string> = {},
) {
  return rooms.filter((room) => {
    const roomAssetName = room.room_asset?.name;

    return !!getRoomAssetId(room) && !(roomAssetName && matchedRoomTypeNameBySalesforceName[roomAssetName]);
  });
}

export interface PackageRoomOption {
  id: string;
  isNew: boolean;
  name: string;
}

/** Every room a package can be mapped to: the property's existing rooms plus the rooms about to be created. */
export function buildPackageRoomOptions(
  roomRateRooms: Array<RoomRateRoom>,
  roomDrafts: Array<FlashOfferRoomDraft>,
): Array<PackageRoomOption> {
  const newRoomKeys = new Set(getCreatableRoomDrafts(roomDrafts).map((draft) => draft.draftId));

  return roomRateRooms.map((room) => ({ id: room.id, isNew: newRoomKeys.has(room.id), name: room.name }));
}

export function mapSalesforceRoomsToRoomDrafts({
  propertyCategory,
  propertyId,
  rooms = [],
}: MapSalesforceRoomsToRoomDraftsParams): Array<FlashOfferRoomDraft> {
  const groupedRooms = new Map<string, Array<SalesforceRoomSource>>();

  rooms.forEach((room) => {
    const groupKey = getRoomAssetDraftId(room);

    if (!groupKey) {
      return;
    }

    groupedRooms.set(groupKey, [...(groupedRooms.get(groupKey) ?? []), room]);
  });

  return Array.from(groupedRooms.entries()).map(([groupKey, sourceRooms]) => {
    const firstRoom = sourceRooms[0];
    const roomName = getRoomDraftName(firstRoom);
    const roomSizeSqm = sourceRooms.map(getSalesforceRoomSizeSqm).find(Boolean) ?? null;
    const bedGroups = getRoomBedGroups(sourceRooms).map((bedGroup) => ({
      ...bedGroup,
      description: getBedGroupDescription(bedGroup),
    }));

    return {
      ...buildBlankRoomDraft({
        draftId: groupKey,
        propertyCategory,
        propertyId,
      }),
      bed_groups: bedGroups,
      name: roomName,
      number_of_beds: bedGroups.length > 0 ? getNumberOfBeds(bedGroups) : [],
      size_sqm: roomSizeSqm,
      shouldCreate: true,
    };
  });
}

export function buildBlankRoomDraft({
  draftId,
  propertyCategory,
  propertyId,
}: BuildBlankRoomDraftParams): FlashOfferRoomDraft {
  return {
    category: propertyCategory ?? null,
    description: '',
    draftId,
    existingRoomName: null,
    name: '',
    property_id: propertyId,
    shouldCreate: true,
    size_sqm: null,
    amenities: [],
    availability_threshold: null,
    bed_groups: [],
    has_live_cm_data: null,
    number_of_bathrooms: null,
    number_of_bedrooms: null,
    number_of_beds: [],
    room_type_code: null,
    source: null,
  };
}

export function getCreatableRoomDrafts(roomDrafts: Array<FlashOfferRoomDraft>) {
  return roomDrafts.filter((roomDraft) => roomDraft.shouldCreate && !roomDraft.existingRoomName);
}

export function buildRoomCreatePayload(roomDraft: FlashOfferRoomDraft, fallbackPropertyId: string): RoomDraftPayload {
  const {
    draftId: _draftId,
    existingRoomName: _existingRoomName,
    room_policies: _roomPolicies,
    shouldCreate: _shouldCreate,
    ...room
  } = roomDraft;

  return {
    ...room,
    description: room.description?.trim() ?? '',
    name: roomDraft.name.trim(),
    property_id: room.property_id || fallbackPropertyId,
  };
}
